import { useCallback, useState } from 'react'
import { useDropzone, type FileRejection } from 'react-dropzone'
import { Upload, X, Loader2, AlertCircle, Image as ImageIcon, Star, CheckCircle } from 'lucide-react'
import { useUploadToStorage, type StagedImage } from '@/hooks'
import { formatBytes, getCompressionRatio } from '@/lib/imageCompression'

// ============================================================================
// Constants
// ============================================================================

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB (before compression)
const ACCEPTED_MIME_TYPES = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'image/avif': ['.avif'],
}

// ============================================================================
// Helper Functions
// ============================================================================

function getErrorMessage(rejection: FileRejection): string {
  const error = rejection.errors[0]
  if (error.code === 'file-too-large') {
    return `El archivo es muy grande (máx. ${formatBytes(MAX_FILE_SIZE)})`
  }
  if (error.code === 'file-invalid-type') {
    return 'Tipo de archivo inválido. Solo se permiten JPEG, PNG, WebP y AVIF.'
  }
  return error.message
}

// ============================================================================
// Props Interface
// ============================================================================

interface StagedImageUploaderProps {
  /** Array of staged images */
  images: StagedImage[]
  /** Callback when images change */
  onImagesChange: (images: StagedImage[]) => void
  /** Whether the uploader is disabled */
  disabled?: boolean
}

// ============================================================================
// Component
// ============================================================================

export function StagedImageUploader({
  images,
  onImagesChange,
  disabled = false,
}: StagedImageUploaderProps) {
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadingCount, setUploadingCount] = useState(0)

  // Hooks
  const uploadToStorage = useUploadToStorage()

  // Handle file drop
  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setUploadError(null)

      // Handle rejected files
      if (rejectedFiles.length > 0) {
        const errorMessages = rejectedFiles.map(
          (rejection) => `${rejection.file.name}: ${getErrorMessage(rejection)}`
        )
        setUploadError(errorMessages.join('; '))
        return
      }

      // Upload all accepted files
      setUploadingCount(acceptedFiles.length)
      const newImages: StagedImage[] = []

      for (const file of acceptedFiles) {
        try {
          const stagedImage = await uploadToStorage.mutateAsync(file)
          newImages.push(stagedImage)
        } catch (error) {
          setUploadError(
            error instanceof Error ? error.message : 'Error al subir imagen'
          )
          break // Stop on first error
        }
      }

      setUploadingCount(0)

      if (newImages.length > 0) {
        onImagesChange([...images, ...newImages])
      }
    },
    [images, onImagesChange, uploadToStorage]
  )

  // Configure dropzone
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: ACCEPTED_MIME_TYPES,
    maxSize: MAX_FILE_SIZE,
    disabled: disabled || uploadToStorage.isPending,
    multiple: true,
  })

  // Handle delete
  const handleDelete = (imageToDelete: StagedImage) => {
    // Revoke the preview URL to free memory
    if (imageToDelete.previewUrl) {
      URL.revokeObjectURL(imageToDelete.previewUrl)
    }
    // Remove from list
    onImagesChange(images.filter((img) => img.id !== imageToDelete.id))
  }

  // Handle set primary - moves the image to position 0
  const handleSetPrimary = (image: StagedImage) => {
    const otherImages = images.filter((img) => img.id !== image.id)
    onImagesChange([image, ...otherImages])
  }

  const isUploading = uploadToStorage.isPending || uploadingCount > 0
  const isBusy = isUploading

  return (
    <section className="rounded-lg border bg-card p-6">
      <h2 className="mb-4 text-lg font-semibold">Imágenes</h2>

      {/* Mostrar Error */}
      {uploadError && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <p className="font-medium">Error de Subida</p>
            <p>{uploadError}</p>
          </div>
          <button
            type="button"
            onClick={() => setUploadError(null)}
            className="ml-auto flex-shrink-0 p-1 hover:opacity-70"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`
          relative cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors
          ${isDragActive && !isDragReject ? 'border-primary bg-primary/5' : ''}
          ${isDragReject ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : ''}
          ${!isDragActive && !disabled ? 'border-muted-foreground/25 hover:border-muted-foreground/50' : ''}
          ${disabled || isBusy ? 'cursor-not-allowed opacity-50' : ''}
        `}
      >
        <input {...getInputProps()} />

        {isUploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">
              {uploadToStorage.isCompressing ? 'Comprimiendo...' : 'Subiendo...'}
              {uploadingCount > 1 && ` (${uploadingCount} archivos)`}
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-10 w-10 text-muted-foreground" />
            <div>
              <p className="font-medium">
                {isDragActive
                  ? isDragReject
                    ? 'Tipo de archivo inválido'
                    : 'Suelta las imágenes aquí'
                  : 'Arrastra y suelta imágenes aquí'}
              </p>
              <p className="text-sm text-muted-foreground">
                o haz clic para seleccionar archivos
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              JPEG, PNG, WebP o AVIF hasta {formatBytes(MAX_FILE_SIZE)} (se optimizarán automáticamente)
            </p>
          </div>
        )}
      </div>

      {/* Cuadrícula de Imágenes Staged */}
      {images.length > 0 ? (
        <div className="mt-6">
          <h3 className="mb-3 text-sm font-medium text-muted-foreground">
            Imágenes para Subir ({images.length})
          </h3>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
            {images.map((image, index) => (
              <StagedImageCard
                key={image.id}
                image={image}
                isPrimary={index === 0}
                onDelete={() => handleDelete(image)}
                onSetPrimary={() => handleSetPrimary(image)}
                disabled={disabled || isBusy}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-6 flex flex-col items-center justify-center py-8 text-center">
          <ImageIcon className="h-12 w-12 text-muted-foreground/50" />
          <p className="mt-2 text-sm text-muted-foreground">
            Aún no hay imágenes seleccionadas
          </p>
          <p className="text-xs text-muted-foreground">
            Las imágenes se guardarán al crear la propiedad
          </p>
        </div>
      )}
    </section>
  )
}

// ============================================================================
// Staged Image Card Component
// ============================================================================

interface StagedImageCardProps {
  image: StagedImage
  isPrimary: boolean
  onDelete: () => void
  onSetPrimary: () => void
  disabled: boolean
}

function StagedImageCard({
  image,
  isPrimary,
  onDelete,
  onSetPrimary,
  disabled,
}: StagedImageCardProps) {
  return (
    <div className="group relative overflow-hidden rounded-lg border bg-muted">
      {/* Image */}
      <div className="aspect-square">
        <img
          src={image.previewUrl}
          alt={image.altText || 'Preview'}
          className="h-full w-full object-cover"
        />
      </div>

      {/* Insignia Principal */}
      {isPrimary && (
        <div className="absolute left-2 top-2 flex items-center gap-1 rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground">
          <Star className="h-3 w-3 fill-current" />
          Principal
        </div>
      )}

      {/* Compression badge */}
      {image.compression && (
        <div className="absolute right-2 top-2 flex items-center gap-1 rounded bg-green-500 px-2 py-1 text-xs font-medium text-white">
          <CheckCircle className="h-3 w-3" />
          -{getCompressionRatio(image.compression.originalSize, image.compression.compressedSize)}
        </div>
      )}

      {/* Overlay with actions */}
      <div
        className={`
          absolute inset-0 flex items-center justify-center gap-2 bg-black/50 transition-opacity
          ${disabled ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'}
        `}
      >
        {/* Botón Establecer Principal */}
        {!isPrimary && (
          <button
            type="button"
            onClick={onSetPrimary}
            disabled={disabled}
            title="Establecer como principal"
            className="rounded-full bg-white p-2 text-gray-700 shadow-lg transition-transform hover:scale-110 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Star className="h-4 w-4" />
          </button>
        )}

        {/* Botón Eliminar */}
        <button
          type="button"
          onClick={onDelete}
          disabled={disabled}
          title="Eliminar imagen"
          className="rounded-full bg-red-500 p-2 text-white shadow-lg transition-transform hover:scale-110 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Información del archivo */}
      <div className="p-2">
        <p className="truncate text-xs text-muted-foreground">
          {image.file.name}
        </p>
        {image.width && image.height && (
          <p className="text-xs text-muted-foreground">
            {image.width} × {image.height} • {formatBytes(image.fileSize ?? 0)}
          </p>
        )}
      </div>
    </div>
  )
}
